import React, { useCallback } from 'react';
import EditShareableLinkContext from './EditShareableLink.context';
import {
  DataRoomShareableLink,
  ShareDataRoomNodes,
} from 'models/ShareableLink';
import ShareableLinkDrawer from '../../components/DataRoomDocumentsRouter/components/ShareableLinkDrawer';
import ShareableFilesSelectModal from '../../components/DataRoomDocumentsRouter/components/ShareableFilesSelectModal';
import useShareableLink from '../../hooks/useShareableLink';
import { DataRoomSelectableNode } from '../../components/FileCheckSelector/types';
import { collectCheckedNodesForShareableLink } from '../../components/FileCheckSelector/utils';
import dataRoomApi from '../../dataRoomApi';
import useCallbackAsync from 'hooks/useCallbackAsync';

type EditShareableLinkProps = {
  updateShareableLink?: (data: DataRoomShareableLink) => void;
};

const EditShareableLink: React.FC<EditShareableLinkProps> = (props) => {
  const { children, updateShareableLink } = props;

  const {
    drawer,
    selectedLink,
    modalNodes,
    isLoading,
    checkedFileIds,
    checkedFolderIds,
    initiallyCheckedNodes,
    drawerRef,
    modalRef,
    setSelectedLink,
    setCheckedFileIds,
    setCheckedFolderIds,
    openModal,
    onCloseDrawer,
    onModalFilesSelect,
  } = useShareableLink();

  const openEditShareableLink = useCallback(
    (link: DataRoomShareableLink) => {
      setSelectedLink(link);
      drawer?.setOpen(true);
    },
    [drawer, setSelectedLink],
  );

  const updateSecureLinkNodes = useCallback(
    ({ fileIds, folderIds }: ShareDataRoomNodes) => {
      setCheckedFileIds(fileIds);
      setCheckedFolderIds(folderIds);
    },
    [setCheckedFileIds, setCheckedFolderIds],
  );

  const [onEditModalFilesSelect, isSubmittingFilesSelect] = useCallbackAsync(
    async (nodes: DataRoomSelectableNode[]) => {
      const payload = collectCheckedNodesForShareableLink(
        nodes,
        checkedFileIds,
      );

      const {
        data: { fileIds, folderIds },
      } = await dataRoomApi.shareableLinks.patchShareableLink(
        selectedLink._id,
        payload,
      );

      onModalFilesSelect(nodes);
      updateSecureLinkNodes({
        fileIds,
        folderIds,
      });
    },
  );

  return (
    <EditShareableLinkContext.Provider value={{ openEditShareableLink }}>
      {children}

      <ShareableLinkDrawer
        ref={drawerRef}
        isEdit
        checkedFileIds={checkedFileIds}
        checkedFolderIds={checkedFolderIds}
        link={selectedLink}
        onFilesReselect={openModal}
        updateSecureLinkNodes={updateSecureLinkNodes}
        onSuccessLink={updateShareableLink}
        closeAction={onCloseDrawer}
      />

      <ShareableFilesSelectModal
        ref={modalRef}
        nodes={modalNodes}
        isLoading={isLoading}
        isSubmitting={isSubmittingFilesSelect}
        initiallyCheckedNodes={initiallyCheckedNodes}
        onFilesSelect={onEditModalFilesSelect}
      />
    </EditShareableLinkContext.Provider>
  );
};

export default EditShareableLink;
